<template>
  <BoxWithTitle
    collapsible
    openingIcon="mdi-chevron-down"
    closingIcon="mdi-chevron-up"
  >
    <template #header>
      <v-toolbar-title>
        {{ $t('maintenanceSchedule.categories') }}
      </v-toolbar-title>
    </template>
    <template #content>
      <VTableRemote
        v-if="!loading"
        ref="tableRemoteRef"
        :iconHeader="hasPermission('maintenanceSchedules.categories.add') ? 'add' : null"
        :items="displayedCategories"
        isSearchable
        :headers="tableHeaders"
        :itemActions="actions"
        :pagination="pagination"
        hideDefaultFooter
        :isCustomIcon="true"
        :isInfinitScroll="true"
        :title="$t('maintenanceSchedule.categories')"
        :centerColumns="['action']"
        :notSortableColumns="['action']"
        :sortableColumns="['name']"
        @iconAction="onClickAction"
        @sendQueryParams="fetchQuery"
      >
        <template #withInfiniteScroll>
          <div v-intersect="endIntersect" />
        </template>
      </VTableRemote>
    </template>
  </BoxWithTitle>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import { isEmpty } from 'lodash';
import ConfirmForm from '@/components/shared/widgets/ConfirmForm';
import CategoryForm from '@/components/forms/CategoryForm';
import BoxWithTitle from '@/components/shared/containers/VBoxWithTitle';
import VTableRemote from '@/components/shared/content-views/VTableRemote';

export default {
    components: {
        BoxWithTitle,
        VTableRemote,
    },
    data() {
        return {
            categories: [],
            loading: false,
            pagination: {},
            queryParams: { perPage: 25 },
        };
    },
    computed: {
        ...mapGetters([
            'hasPermission',
            'userLang',
            'teamMainLang',
        ]),
        tableHeaders() {
            return [
                'name',
            ].map(header => ({
                text: this.$t(`forms.labels.${header}`),
                value: header,
                sortable: true,
            }));
        },
        displayedCategories() {
            return this.categories.map(({ id, name, active }) => ({
                id,
                name: name[this.userLang] ? name[this.userLang] : name[this.teamMainLang],
                active,
            }));
        },
        actions() {
            const actions = [];

            if (this.hasPermission('maintenanceSchedules.categories.edit')) {
                actions.push({
                    name: this.$t('forms.btn.edit'),
                    icon: 'edit',
                    mode: 'edit',
                });
            }

            if (this.hasPermission('maintenanceSchedules.categories.remove')) {
                actions.push({
                    name: this.$t('forms.btn.delete'),
                    icon: 'delete',
                    mode: 'delete',
                    iconAndTextColor: '#EF4135',
                });
            }

            return actions;
        },
    },
    async created() {
        this.loading = true;
        await this.getCategories();
        this.loading = false;
    },
    methods: {
        async getCategories() {
            // for the first time fetch (empty pagination) OR next page
            if (isEmpty(this.pagination) || (this.pagination?.currentPage !== this.queryParams?.page)) {
                const { data } = await this.$rq.getMaintenanceScheduleCategories(this.queryParams);
                this.categories = [...data.categories];
                this.pagination = data.pagination;
            }
        },
        async fetchQuery({ order, ...params }) {
            this.queryParams = {
                ...this.queryParams,
                ...params,
                page: 1,
                order,
            };
            this.pagination = {};
            this.categories = [];
            await this.getCategories();
        },

        onClickAdd() {
            this.$root.$emit('showModal', {
                title: this.$t('maintenanceSchedule.addCategory'),
                component: Vue.component('CategoryForm', CategoryForm),
                showLangs: true,
                onConfirm: requestPayload => this.addCategory(requestPayload),
            });
        },
        onClickAction(category, action) {
            if (category === 'add') {
                this.onClickAdd();
            } else if (action.mode === 'edit') {
                this.$root.$emit('showModal', {
                    title: this.$t('maintenanceSchedule.updateCategory'),
                    component: Vue.component('CategoryForm', CategoryForm),
                    payload: { category: this.categories.find(cat => cat.id === category.id), isEdition: true },
                    showLangs: true,
                    onConfirm: requestPayload => this.updateCategory(category.id, requestPayload),
                });
            } else if (action.mode === 'delete') {
                this.$root.$emit('showModal', {
                    title: this.$t('maintenanceSchedule.removeCategory'),
                    component: Vue.component('ConfirmForm', ConfirmForm),
                    payload: { msg: this.$t('maintenanceSchedule.confirmRemoveCategory') },
                    onConfirm: () => this.removeCategory(category.id),
                    isForm: false,
                });
            }
        },
        async addCategory(requestPayload) {
            await this.$rq.addMaintenanceScheduleCategory(requestPayload);
            this.$toasted.global.toast_success({
                message: this.$t('msg.categoryAdded'),
            });
            await this.getCategories();
        },
        async updateCategory(id, requestPayload) {
            if (!requestPayload) return;
            await this.$rq.updateMaintenanceScheduleCategory(id, requestPayload);
            this.$toasted.global.toast_success({
                message: this.$t('msg.categoryModified'),
            });
            await this.getCategories();
        },
        async removeCategory(id) {
            await this.$rq.removeMaintenanceScheduleCategory(id);
            this.$toasted.global.toast_success({
                message: this.$t('msg.categoryRemoved'),
            });
            await this.getCategories();
        },
        async endIntersect(_, __, isIntersecting) {
            const condition = this.pagination.currentPage < this.pagination.totalPageCount;
            if (isIntersecting && condition) {
                this.queryParams.page = this.pagination.currentPage + 1;
                await this.getCategories();
                const ref = this.$refs?.tableRemoteRef?.$refs?.tableRemote;
                if (ref?.onScroll) {
                    ref.onScroll();
                }
            }
        },
    },
};
</script>
